import * as Sentry from "@sentry/react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import jobSearchApi from "../../api/jobSearch";
import Button from "../common/Button";
import FeaturedJobCard from "../ui/FeaturedJobCard";
import SearchFilter from "../common/SearchFilter";
import { useQuery } from "@tanstack/react-query";
import { buildQueryPayload } from "../../utils/helpers";

const FeaturedJobsSection = ({ onNavigate }) => {
  const [filters, setFilters] = useState({
    ordering: "newest",
    page_size: 6,
    pilot_type: "",
    total_flight_hours: "",
  });


  const cleanedFilters = useMemo(() => {
  return buildQueryPayload(filters)
}, [filters]);

  const {
    data: featuredJobs,
    isError,
    error,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ["featuredJobs", filters],
    queryFn: async () => {
      const response = await jobSearchApi.searchJobs(cleanedFilters);
      return response.data;
    },
    keepPreviousData: true,
  });

  const renderContent = () => {
  if (isLoading || isFetching) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => (
          <JobCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    Sentry.captureException(error, "Failed to fetch featured jobs");
    return (
      <div className="text-center text-red-600 bg-red-50 p-6 rounded-lg border border-red-200">
        Could not load featured jobs at this time.
      </div>
    );
  }

  if (!featuredJobs?.results?.length) {
    const hasActiveFilters =
      filters.pilot_type || filters.total_flight_hours;

    return (
      <EmptyState
        hasActiveFilters={hasActiveFilters}
        onReset={() =>
          setFilters((prev) => ({
            ...prev,
            pilot_type: "",
            total_flight_hours: "",
          }))
        }
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: { staggerChildren: 0.1 },
        },
      }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
    >
      {featuredJobs.results.map((job) => (
        <motion.div
          key={job.id}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          <FeaturedJobCard job={job} onNavigate={onNavigate} />
        </motion.div>
      ))}
    </motion.div>
  );
};

  return (
    <section id="jobs" className="py-20 bg-secondary-100">
      <div className="container mx-auto px-6">
        {/* Section Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-3">
            Featured Opportunities
          </h2>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            Hand-picked aviation positions from leading companies worldwide.
          </p>
          <SearchFilter searchFilters={filters} setSearchFilters={setFilters} />
        </motion.div>

        {/* Jobs List with stagger animation */}
        {renderContent()}

        {/* CTA */}
        <div className="text-center mt-16">
          <Button
            variant="primary"
            size="lg"
            onClick={() => onNavigate("/job-search")}
            icon={<Search />}
          >
            Explore All Jobs
          </Button>
        </div>
      </div>
    </section>
  );
};

const JobCardSkeleton = () => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 animate-pulse">
    <div className="flex items-start space-x-4 mb-4">
      <div className="w-14 h-14 rounded-xl bg-gray-200 flex-shrink-0"></div>
      <div className="flex-1 min-w-0">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-5 bg-gray-300 rounded w-full"></div>
      </div>
    </div>
    <div className="space-y-3 mt-6">
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
    </div>
    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
      <div className="h-5 bg-gray-200 rounded w-1/4"></div>
      <div className="h-5 bg-gray-300 rounded w-1/3"></div>
    </div>
  </div>
);

const EmptyState = ({ hasActiveFilters, onReset, onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="text-center py-20"
  >
    <div className="mx-auto w-20 h-20 rounded-full bg-secondary-200 flex items-center justify-center mb-6">
      <Search className="w-8 h-8 text-secondary-500" />
    </div>

    <h3 className="text-2xl font-semibold text-secondary-900 mb-3">
      {hasActiveFilters
        ? "No jobs match your filters"
        : "No featured jobs available"}
    </h3>

    <p className="text-secondary-600 max-w-md mx-auto mb-6">
      {hasActiveFilters
        ? "Try adjusting your pilot type or flight hours to discover more opportunities."
        : "We’re currently updating our featured listings. Check back shortly or explore all available jobs."}
    </p>

    <div className="flex justify-center gap-4 flex-wrap">
      {hasActiveFilters && (
        <Button variant="outline" size="md" onClick={onReset}>
          Clear Filters
        </Button>
      )}

      <Button
        variant="primary"
        size="md"
        onClick={() => onNavigate("/job-search")}
      >
        Browse All Jobs
      </Button>
    </div>
  </motion.div>
);

export default FeaturedJobsSection;
